import { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useGenieCalendarList, useSelectGenieCalendar } from "@/hooks/agent/useGenieMeetings";
import { Agent } from "@/types/agent/agent";

interface MeetingCalendarPickerProps {
  agent: Agent;
  brand: string;
  connectionId: string;
  currentCalendarId: string | null;
  /** Shown when a calendar is already picked, to keep it. */
  onCancel?: () => void;
  onPicked: () => void;
}

/** Picks which of the connected account's calendars the genie joins meetings from. */
export const MeetingCalendarPicker = ({
  agent,
  brand,
  connectionId,
  currentCalendarId,
  onCancel,
  onPicked,
}: MeetingCalendarPickerProps) => {
  const { data: calendars = [], isLoading, isError, refetch } = useGenieCalendarList(agent.id, connectionId, true);
  const selectMutation = useSelectGenieCalendar(agent.id, connectionId);
  const [chosenId, setChosenId] = useState<string | null>(currentCalendarId);

  useEffect(() => {
    if (!chosenId && calendars.length)
      setChosenId(calendars.find((calendar) => calendar.is_primary)?.id ?? calendars[0].id);
  }, [calendars, chosenId]);

  const softButtonStyle = { backgroundColor: `${brand}15`, color: brand };

  const handleConfirm = async () => {
    if (!chosenId) return;
    try {
      const connection = await selectMutation.mutateAsync(chosenId);
      toast.success(`${agent.name} will join meetings from ${connection.calendar_name ?? "this calendar"}`);
      onPicked();
    } catch (err: any) {
      toast.error(err.message ?? "That calendar couldn't be picked");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 py-2">
        <Loader2 className="h-4 w-4 animate-spin text-genie-primary" />
        <span className="text-sm text-gray-600">Loading your calendars...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <p className="text-sm text-gray-600 flex-1">Your calendars couldn't be loaded.</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="shrink-0 min-h-[44px] px-4 rounded-lg text-sm font-medium transition-colors"
          style={softButtonStyle}
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-semibold text-gray-700">Which calendar should {agent.name} join meetings from?</p>
        <p className="text-xs text-gray-500 mt-0.5">It only reads this calendar's events and never changes them.</p>
      </div>

      <ul className="space-y-2" role="radiogroup" aria-label="Calendar">
        {calendars.map((calendar) => {
          const active = chosenId === calendar.id;
          return (
            <li key={calendar.id}>
              <button
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setChosenId(calendar.id)}
                className="w-full min-h-[44px] flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-colors"
                style={
                  active
                    ? {
                        borderColor: `${brand}55`,
                        backgroundColor: `${brand}08`,
                      }
                    : { borderColor: "#E5E7EB" }
                }
              >
                <span
                  className="h-3 w-3 shrink-0 rounded-full"
                  style={{ backgroundColor: calendar.hex_color || brand }}
                  aria-hidden="true"
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-gray-900 truncate">{calendar.name}</span>
                  {calendar.is_primary && <span className="block text-xs text-gray-500">Main calendar</span>}
                </span>
                {active && <Check className="h-4 w-4 shrink-0" style={{ color: brand }} />}
              </button>
            </li>
          );
        })}
      </ul>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!chosenId || selectMutation.isPending}
          className="min-h-[44px] px-4 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 inline-flex items-center gap-2"
          style={softButtonStyle}
        >
          {selectMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Use this calendar
        </button>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} className="min-h-[44px]">
            Keep the current one
          </Button>
        )}
      </div>
    </div>
  );
};
